import cn from 'classnames';
import { ReactNode, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Button } from '@Components/Button/Button';
import { Appearance, Placement, Size, StretchMode } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import {
  AnchorStatus,
  AnchorStatusType,
  BUDGET_PERIOD_LABELS,
  BudgetPeriod,
  BudgetPeriodType,
  Card,
  SCALING_QUICK_PICK_MULTIPLES
} from './request-form-constants';
import {
  checkMaxWeeklySpend,
  formatDollars,
  formatWeekOf,
  HighestWeek,
  totalToWeekly,
  weeklyToTotal
} from './scaling-request-utils';

const PERIOD_OPTIONS = Object.values(BudgetPeriod).map(period => ({
  value: period,
  label: BUDGET_PERIOD_LABELS[period]
}));

// Whole dollars for display; the stored weekly amount keeps its precision so a
// total typed against a month round-trips exactly.
const displayDollars = (n: number): string => (Number.isFinite(n) ? String(Math.round(n)) : '');

interface ScalingBudgetFieldsProps {
  maxWeeklySpend: string;
  budgetPeriod: BudgetPeriodType;
  /** Highest full week of spend at the card's grain; null until (or unless) the series file loads. */
  highestWeek: HighestWeek | null;
  anchorStatus: AnchorStatusType;
  /** Re-fetches the series after AnchorStatus.FAILED. */
  onRetryAnchor: () => void;
  onChange: (patch: Partial<Pick<Card, 'maxWeeklySpend' | 'budgetPeriod'>>) => void;
}

/**
 * A scaling card's "Max weekly budget to test": Total budget | Budget period |
 * = Weekly budget, linked both ways like the comparison rows' "% = $" pair.
 * Weekly is the stored value; changing the period keeps it and re-derives the total.
 */
export const ScalingBudgetFields = ({
  maxWeeklySpend,
  budgetPeriod,
  highestWeek,
  anchorStatus,
  onRetryAnchor,
  onChange
}: ScalingBudgetFieldsProps): ReactNode => {
  // Drafts show exactly what's typed while a field is focused, instead of a
  // value re-derived from the other field on every keystroke (see ComparisonBudgetRow).
  const [weeklyDraft, setWeeklyDraft] = useState<string | null>(null);
  const [totalDraft, setTotalDraft] = useState<string | null>(null);

  const weekly = maxWeeklySpend === '' ? NaN : Number(maxWeeklySpend);
  // Before the prefill lands, an empty field is "loading", not a user error.
  const check =
    maxWeeklySpend === '' && anchorStatus === AnchorStatus.LOADING
      ? null
      : checkMaxWeeklySpend(maxWeeklySpend, highestWeek?.spend);

  return (
    <div className='req-scaling-budget' data-testid='scaling-budget'>
      <span className='req-label'>
        Max weekly budget to test <span className='req-required'>*</span>
      </span>
      <p className='req-scaling-instruction'>
        Enter the most you&apos;d want to spend in a week. We&apos;ll show the expected results at
        every weekly spend level, from a typical week for this channel up to this amount.
      </p>

      <div className='req-scaling-row'>
        <div className='req-scaling-field'>
          <span className='req-sublabel'>Total budget</span>
          <div className='req-scaling-control req-money'>
            <span>$</span>
            <Form.Control
              type='number'
              min={0}
              aria-label='Total budget'
              data-testid='scaling-total-budget'
              value={totalDraft ?? displayDollars(weeklyToTotal(weekly, budgetPeriod))}
              onChange={e => {
                const raw = e.target.value;
                setTotalDraft(raw);
                const total = Number(raw);
                // Only commit a value that parses, so "" mid-edit doesn't wipe the weekly amount.
                if (raw !== '' && Number.isFinite(total)) {
                  onChange({ maxWeeklySpend: String(totalToWeekly(total, budgetPeriod)) });
                }
              }}
              onBlur={() => setTotalDraft(null)}
            />
          </div>
        </div>
        <div className='req-scaling-field'>
          <span className='req-sublabel'>Budget period</span>
          <div className='req-scaling-control' data-testid='scaling-budget-period'>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              size={Size.LG}
              stretch={StretchMode.FIT}
              value={budgetPeriod}
              options={PERIOD_OPTIONS}
              onChange={(period: BudgetPeriodType) => onChange({ budgetPeriod: period })}
            />
          </div>
        </div>
        <span className='req-eq'>=</span>
        <div className='req-scaling-field'>
          <span className='req-sublabel'>Weekly budget</span>
          <div className='req-scaling-control req-money'>
            <span>$</span>
            <Form.Control
              type='number'
              min={0}
              aria-label='Weekly budget'
              data-testid='scaling-weekly-budget'
              value={weeklyDraft ?? displayDollars(weekly)}
              onChange={e => {
                const raw = e.target.value;
                setWeeklyDraft(raw);
                if (raw !== '' && Number.isFinite(Number(raw))) {
                  onChange({ maxWeeklySpend: raw });
                }
              }}
              onBlur={() => setWeeklyDraft(null)}
            />
          </div>
        </div>
      </div>
      <span className='req-hint'>
        Enter either amount and we&apos;ll fill in the other. The period is how long the total
        budget covers. If the budget isn&apos;t spread evenly, use your biggest week.
      </span>

      <div className='req-scaling-note'>
        <strong>Results are shown per week.</strong> Each point is what one week at that spend level
        is expected to bring in. Scaling isn&apos;t tied to the forecast period above; it uses all
        of this combo&apos;s past data.
      </div>

      {highestWeek && (
        <>
          <div className='req-scaling-anchor' data-testid='scaling-anchor'>
            Your highest week in this data: <strong>{formatDollars(highestWeek.spend)}</strong>{' '}
            (week of {formatWeekOf(highestWeek.weekStart)})
          </div>
          <div className='req-scaling-quick' aria-label='Quick picks'>
            {SCALING_QUICK_PICK_MULTIPLES.map(multiple => (
              <Button
                key={multiple}
                size={Size.SM}
                appearance={Appearance.OUTLINED}
                onClick={() =>
                  onChange({ maxWeeklySpend: String(Math.round(highestWeek.spend * multiple)) })
                }
              >
                {`${multiple}x · ${formatDollars(highestWeek.spend * multiple)}`}
              </Button>
            ))}
          </div>
        </>
      )}
      {!highestWeek && anchorStatus === AnchorStatus.LOADING && (
        <span className='req-hint'>Finding your highest week in this data…</span>
      )}
      {!highestWeek && anchorStatus === AnchorStatus.UNAVAILABLE && (
        <span className='req-hint'>
          Your highest week isn&apos;t available for this Data QA run, so we can&apos;t check the
          amount against it.
        </span>
      )}
      {!highestWeek && anchorStatus === AnchorStatus.FAILED && (
        <div className='req-scaling-retry' data-testid='scaling-anchor-failed'>
          <span className='req-hint'>Couldn&apos;t load your highest week.</span>
          <Button size={Size.SM} appearance={Appearance.OUTLINED} onClick={onRetryAnchor}>
            Try again
          </Button>
        </div>
      )}

      {check && (
        <div
          className={cn('req-scaling-msg', check.level)}
          role={check.level === 'error' ? 'alert' : 'status'}
          data-testid='scaling-spend-check'
        >
          {check.message}
        </div>
      )}
    </div>
  );
};
